import { FC, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowCircleLeft';

import { IUnitsInformation } from '../../components/UnitsInformation';
import { IFormData } from './type';
import { InformationPair } from '../../components/InformationPair';
import { Forms } from './Forms';
import { useUnitInformation } from '../../context/UnitsInformationProvider';
import { ReservationInformation } from './ReservationInformation';
import { useConfig } from '../../context/ConfigProvider';
import { GetUnitGroupSummary } from '../../services/UnitGroups';
import { getFacilityAlias } from '../../utils/Aliasing';
import { UnitGroup } from '../../types/UnitGroup';
import { createUnitsGroup } from '../../components/UnitsInformation/type';
import formatFloorData from '../../utils/FormatFloor';
import { getProperValue } from '../../components/UnitsInformation/subComponents/Pricing';
import { getAllImages } from '../../utils/UnitImages';

interface IReservationForm {
  selectedUnit: IUnitsInformation;
  onBackClick: () => void;
}

export const ReservationForm: FC<IReservationForm> = (props) => {
  const {
    reserve: makeReservation,
    reservation,
    reservationRequest,
    navigateToOMI,
    clearReservation,
  } = useUnitInformation();
  const { widgetParams, configuration } = useConfig();
  const [unitGroupSummary, setUnitGroupSummary] = useState<
    UnitGroup | undefined
  >();

  const typeOfForm = useMemo(() => {
    switch (true) {
      case props.selectedUnit.availbleUnits <=
        configuration.contactForReserveUnitThreshold:
        return 'contactUs';
      default:
        return 'reservation';
    }
  }, [props.selectedUnit, configuration.contactForReserveUnitThreshold]);

  const getImageSet = getAllImages(props.selectedUnit?.images, props.selectedUnit)

  useEffect(() => {
    GetUnitGroupSummary(
      widgetParams.accessKey,
      getFacilityAlias(widgetParams, true),
      props.selectedUnit.id
    ).then((resp) => {
      setUnitGroupSummary(resp);
    });
  }, [widgetParams, props.selectedUnit.id]);

  const reserve = (data: IFormData, navigate: boolean = true) => {
    makeReservation(data, navigate, {
      unitGroupId: props.selectedUnit.id,
      typeOfForm,
      source: widgetParams?.source || 'OMI',
    });
  };

  const priceUnit =
    reservation && typeOfForm !== 'contactUs'
      ? createUnitsGroup(
          reservation.unit,
          configuration
        )
      : props.selectedUnit;

  return (
    <div>
      <Box
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <Button
          sx={{ padding: 0, marginLeft: -1.25 }}
          onClick={() => {
            props.onBackClick();
            clearReservation();
          }}
        >
          <ArrowBackIcon />
          Back
        </Button>
      </Box>
      <Grid container spacing={2}>
        {typeOfForm === 'contactUs' && (
          <Grid item xs={12}>
            <Typography variant='h6'>
              Please contact us regarding the availability of this unit
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} lg={6}>
          <Typography variant='h5' gutterBottom>
            {props.selectedUnit.name ||
              `${props.selectedUnit.size.length}’ x ${props.selectedUnit.size.width}’`}
          </Typography>
          <InformationPair
            data={[
              {
                label: 'Size',
                value: `${props.selectedUnit?.size?.width}ft x ${props.selectedUnit?.size?.length}ft (${props?.selectedUnit?.area} sq ft)`,
              },
              {
                label: 'Amenities',
                value: props.selectedUnit.amenities.join(', '),
              },
              {
                label: 'Location',
                value: [
                  unitGroupSummary?.accessType,
                  formatFloorData(unitGroupSummary?.floor),
                  unitGroupSummary?.unitEnclosureStatus,
                ]
                  .filter((x) => Boolean(x))
                  .join(', '),
              },
              ...(typeOfForm !== 'contactUs' &&
              props.selectedUnit.tiers.length === 0 &&
              !reservation
                ? [
                    {
                      label: 'Price',
                      value: priceUnit.discountedPriceStart ? (
                        <Box
                          display='flex'
                          flexDirection='row'
                          alignItems='center'
                        >
                          <Typography
                            variant='body2'
                            sx={(theme) => ({
                              textDecoration: 'line-through',
                              color: theme.palette.grey[500],
                            })}
                          >{`${
                            props.selectedUnit.currencySymbol
                          }${getProperValue(
                            priceUnit.startPrice, configuration?.roundOffUnitDisplayPrice
                          )}`}</Typography>
                          <Typography variant='body2'>{`${
                            props.selectedUnit.currencySymbol
                          }${getProperValue(priceUnit.discountedPriceStart, configuration?.roundOffUnitDisplayPrice)}/${
                            props.selectedUnit.pricePer
                          }`}</Typography>
                        </Box>
                      ) : (
                        `${props.selectedUnit.currencySymbol}${getProperValue(
                          priceUnit.startPrice, configuration?.roundOffUnitDisplayPrice
                        )}/${props.selectedUnit.pricePer}`
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Box display='flex' flexDirection='row' flexWrap='wrap'>
            {getImageSet.map((image, index) => (
              <Box
                key={index}
                sx={(theme) => ({
                  height: theme.spacing(16),
                  width: theme.spacing(16),
                  margin: theme.spacing(1)
                })}
              >
                <img
                  src={image || 'https://placehold.co/150'}
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'contain',
                  }}
                  alt={props.selectedUnit.name || 'Unit Group Image'}
                />
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
      {reservation && reservationRequest ? (
        <ReservationInformation
          typeOfForm={typeOfForm}
          reservationInformation={reservation}
          reservationRequest={reservationRequest}
          onMoveInClick={() => navigateToOMI(reservation, true)}
          onFindAnotherUnitClick={() => {
            props.onBackClick();
            clearReservation();
          }}
        />
      ) : (
        <Forms
          unitGroupInformation={props.selectedUnit}
          unitGroupSummary={unitGroupSummary}
          typeOfForm={typeOfForm}
          onFormDataSubmit={(information) => {
            reserve(information, true);
          }}
          onFormDataSecondarySubmit={(information) => {
            reserve(information, false);
          }}
          formData={{} as IFormData}
        />
        // <Selection
        //   formType={typeOfForm}
        //   selectedUnit={props.selectedUnit}
        //   selectedTier={selectedTier}
        //   onSelectTier={selectTier}
        //   onFormSubmit={(information) => {
        //     reserve(information, true);
        //   }}
        //   onFormSecondarySubmit={(information) => {
        //     reserve(information, false);
        //   }}
        // />
      )}
    </div>
  );
};
